import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Maximize2, ExternalLink, Check, Loader2 } from 'lucide-react'
import { getDocumentListItems } from '../../services/api'
import InlineConnectionsPanel from '../graph/InlineConnectionsPanel'
import type { Document } from '../../types'

interface ConnectionsListViewProps {
  document: Document
}

interface ConnectionItem {
  id: string
  type: 'journal' | 'contact' | 'organisation'
  label: string
  score?: number
  saved: boolean
  doi?: string
}

const TYPE_COLORS: Record<string, string> = {
  journal: 'bg-purple-50 text-purple-600',
  contact: 'bg-blue-50 text-blue-600',
  organisation: 'bg-orange-50 text-orange-600',
}

const TYPE_LABELS: Record<string, string> = {
  journal: 'Journal',
  contact: 'Contact',
  organisation: 'Org',
}

export default function ConnectionsListView({ document }: ConnectionsListViewProps) {
  const [showGraph, setShowGraph] = useState(false)

  const { data: savedData, isLoading } = useQuery({
    queryKey: ['document-list', document.document_id],
    queryFn: () => getDocumentListItems(document.document_id),
  })

  const connections = useMemo(() => {
    const items: ConnectionItem[] = []
    const seenIds = new Set<string>()

    // AI-discovered journals from embeddings
    if (document.related_journals) {
      for (const j of document.related_journals) {
        const jid = j.id
        if (!seenIds.has(jid)) {
          seenIds.add(jid)
          items.push({
            id: jid,
            type: 'journal',
            label: j.title,
            score: j.score,
            saved: false,
            doi: j.doi,
          })
        }
      }
    }

    // Saved items from document lists
    if (savedData?.items) {
      for (const item of savedData.items) {
        if (seenIds.has(item.resource_id)) {
          // Mark the AI-discovered one as also saved
          const existing = items.find((i) => i.id === item.resource_id)
          if (existing) existing.saved = true
        } else {
          seenIds.add(item.resource_id)
          items.push({
            id: item.resource_id,
            type: item.resource_type,
            label: item.title,
            saved: true,
          })
        }
      }
    }

    return items
  }, [document.related_journals, savedData])

  const aiCount = connections.filter((c) => c.score != null && !c.saved).length
  const savedCount = connections.filter((c) => c.saved).length

  // Graph pop-out
  if (showGraph) {
    return (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            Document Connections
          </h3>
          <button
            onClick={() => setShowGraph(false)}
            className="text-xs text-iflow-600 hover:text-iflow-700 font-medium"
          >
            Back to List View
          </button>
        </div>
        <InlineConnectionsPanel resourceId={document.document_id} title="Document Connections" projectId={document.project_id} />
      </div>
    )
  }

  return (
    <div className="light-card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          Document Connections
          {connections.length > 0 && (
            <span className="text-xs font-normal text-gray-500">
              {connections.length} connection{connections.length !== 1 ? 's' : ''}
              {aiCount > 0 && savedCount > 0 && ` (${aiCount} AI-discovered, ${savedCount} saved)`}
            </span>
          )}
        </h3>
        <button
          onClick={() => setShowGraph(true)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-sky-200 rounded-lg hover:bg-white/50 text-gray-600"
        >
          <Maximize2 className="w-3.5 h-3.5" />
          Open Graph View
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-gray-500" /></div>
      ) : connections.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No connections found. Upload documents to generate embedding-based connections, or save contacts and organisations.</p>
      ) : (
        <div className="space-y-1">
          {connections.map((item) => (
            <div key={item.id} className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-white/30">
              <div className={`w-1 h-8 rounded-full ${item.saved ? 'bg-iflow-500' : 'bg-sky-200'}`} />
              <span className={`text-xs px-2 py-0.5 rounded font-medium w-16 text-center ${TYPE_COLORS[item.type] || 'bg-sky-500/10 text-gray-600'}`}>
                {TYPE_LABELS[item.type] || item.type}
              </span>
              <span className="text-sm text-gray-900 flex-1 truncate">{item.label}</span>
              {item.score != null && (
                <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                  item.score > 0.8 ? 'bg-green-100 text-green-700' :
                  item.score > 0.6 ? 'bg-amber-100 text-amber-700' :
                  'bg-sky-500/10 text-gray-600'
                }`}>
                  {(item.score * 100).toFixed(0)}%
                </span>
              )}
              {item.doi && (
                <a href={`https://doi.org/${item.doi}`} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-iflow-500">
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {item.saved && (
                <span className="text-xs text-green-600 flex items-center gap-0.5">
                  <Check className="w-3 h-3" /> Saved
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {connections.length > 0 && (
        <div className="mt-3 pt-3 border-t border-sky-200/30 flex items-center gap-4 text-xs text-gray-500">
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-1 bg-sky-200 rounded" /> AI-discovered</span>
          <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-1 bg-iflow-500 rounded" /> Saved</span>
        </div>
      )}
    </div>
  )
}
